import { Box, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useMemo, useState } from 'react';
import type { EconSectorCategory } from '../api/chartDataTypes';
import { perf10kSelectSx } from '../styles/chartReader.styles';
import { LabelTypography, Perf10kSectionTitleTypography } from '../styles/chartReader.typography';

const menuPaperSx = {
    border: '1px solid #E4E4E4',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
};

const MENU_PROPS = { PaperProps: { sx: menuPaperSx } };

type SectorSelectorProps = {
    /** Snapshot categories from the econ_sector payload. Order is irrelevant —
     *  the selector sorts by `value` descending internally. */
    categories: EconSectorCategory[];
    /** Prefix for the Sector `<select>` id so multiple snapshot selectors on a
     *  page never collide. */
    idPrefix: string;
    /** Fires when the user picks a sector. Selecting the same sector again
     *  still fires so the parent can re-announce. */
    onSelectedCategoryChange: (category: EconSectorCategory) => void;
};

/**
 * Single-dropdown selector for point-in-time chart snapshots whose categories
 * are identified by a name string rather than a date. Mirrors the Date branch
 * of DataPointSelector — fires the parent's callback on each pick so the
 * shell's live region re-announces the new summary.
 */
export function SectorSelector({ categories, idPrefix, onSelectedCategoryChange }: SectorSelectorProps) {
    const [selectedSector, setSelectedSector] = useState<string>('');

    // Alphabetical ascending by sector name so NVDA users can predict where
    // each sector sits in the list.
    const sortedCategories = useMemo(() => {
        return [...categories].sort((a, b) => a.sector.localeCompare(b.sector));
    }, [categories]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const nextSector = event.target.value;
            setSelectedSector(nextSector);
            const match = sortedCategories.find((c) => c.sector === nextSector);
            if (match) onSelectedCategoryChange(match);
        },
        [sortedCategories, onSelectedCategoryChange],
    );

    const sectorId = `${idPrefix}-sector-select`;

    return (
        <Box>
            <Perf10kSectionTitleTypography>Read a specific sector</Perf10kSectionTitleTypography>

            <Box sx={{ mt: 1.5 }}>
                <label htmlFor={sectorId}>
                    <LabelTypography sx={{ mb: 0.5 }}>Sector</LabelTypography>
                </label>
                <FormControl fullWidth size='small' sx={perf10kSelectSx}>
                    <Select<string>
                        id={sectorId}
                        value={selectedSector}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(v) => (v ? v : 'Select sector')}
                        inputProps={{ 'aria-label': selectedSector === '' ? 'Select sector' : 'Sector' }}
                        MenuProps={MENU_PROPS}>
                        {sortedCategories.map((c) => (
                            <MenuItem key={c.sector} value={c.sector}>
                                {c.sector}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}
